import React, { useEffect, useRef, useState } from 'react';
import { Check, Calendar } from 'lucide-react';
import { getRelevantClusters, fireTrackingEvent } from './intakeData';

const IntakeResults = ({
  firstName,
  email,
  answers,
  assessmentId,
  onScheduleCall,
  onStartMembership,
  onReturnHome,
}) => {
  const relevantClusters = getRelevantClusters(answers);
  const nextStepsRef = useRef(null);
  const [nextStepsViewed, setNextStepsViewed] = useState(false);

  // Fire NextStepsViewed when section enters viewport
  useEffect(() => {
    if (nextStepsViewed) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fireTrackingEvent('NextStepsViewed', {
              assessment_id: assessmentId,
              source: 'post_assessment'
            });
            setNextStepsViewed(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );
    if (nextStepsRef.current) observer.observe(nextStepsRef.current);
    return () => observer.disconnect();
  }, [assessmentId, nextStepsViewed]);

  return (
    <div className="assessment-results">
      <div className="assessment-results-content">
        <div className="results-header results-header-compact">
          <div className="results-header-icon">
            <Check size={32} />
          </div>
          <h1 className="results-title">Assessment Complete</h1>
          <p className="results-subtitle-compact">
            Thank you, {firstName}! Based on your responses, your symptoms may align with
            common hormone-related patterns.
          </p>
        </div>

        <div className="results-email-notice">
          <strong>Important:</strong> We just emailed your results. If you don't see it within
          a few minutes, please check your spam or promotions folder.
        </div>

        <div className="results-next-steps-hero" ref={nextStepsRef} data-testid="next-steps-section">
          <h2 className="next-steps-title">Next Steps <span className="optional-tag">(Optional)</span></h2>

          <div className="next-steps-primary-card">
            <div className="primary-card-content">
              <h3 className="primary-card-title">Schedule a Free Eligibility Call</h3>
              <p className="primary-card-description">
                A brief 10-minute call to review your assessment and see if personalized
                hormone care is right for you.
              </p>
              <ul className="primary-card-features">
                <li><Check size={16} /> No payment required to book</li>
                <li><Check size={16} /> No medical advice on this call</li>
                <li><Check size={16} /> No obligation to proceed</li>
              </ul>
              <button
                className="btn-next-steps-primary"
                onClick={onScheduleCall}
                data-testid="schedule-call-btn"
              >
                <Calendar size={20} />
                Schedule Eligibility Call
              </button>
            </div>
          </div>

          <div className="next-steps-secondary">
            <p className="secondary-text">
              Already know you're ready?
              <button
                className="btn-text-link"
                onClick={onStartMembership}
                data-testid="start-membership-btn"
              >
                Start your membership now →
              </button>
            </p>
          </div>
        </div>

        <div className="results-divider">
          <span>Your Assessment Summary</span>
        </div>

        <div className="results-summary-section">
          <div className="results-insight-card">
            <p className="results-insight-text">
              This assessment is for <strong>educational purposes only</strong> and is not a medical diagnosis.
              It's designed to help you understand common hormone-related patterns and consider whether
              a conversation with a qualified provider might be beneficial.
            </p>
          </div>

          {relevantClusters.length > 0 && (
            <div className="results-clusters">
              <h2 className="results-section-title">Your Symptom Patterns</h2>
              <div className="results-clusters-grid">
                {relevantClusters.map((cluster, index) => {
                  const IconComponent = cluster.icon;
                  return (
                    <div key={index} className="results-cluster-card">
                      <div className="cluster-header">
                        <div className="cluster-icon">
                          <IconComponent size={24} />
                        </div>
                        <h3 className="cluster-title">{cluster.title}</h3>
                      </div>
                      <p className="cluster-description">{cluster.description}</p>
                      <p className="cluster-pattern">{cluster.pattern}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="results-education">
            <h2 className="results-section-title">What This Means</h2>
            <div className="education-content">
              <p>
                Hormones are powerful chemical messengers that influence nearly every aspect of your
                health, from energy and weight to mood, sleep, and cognitive function. When they're
                out of balance, even slightly, you may experience symptoms that affect your quality of life.
              </p>
              <p>
                The good news is that hormone imbalances are often addressable with the right approach.
                Understanding your unique pattern is the first step toward feeling like yourself again.
              </p>
            </div>
          </div>

          <div className="results-trust">
            <div className="trust-item">
              <Check size={18} />
              <span>This assessment is not a medical diagnosis</span>
            </div>
            <div className="trust-item">
              <Check size={18} />
              <span>Your information is private and secure</span>
            </div>
            <div className="trust-item">
              <Check size={18} />
              <span>No obligation to proceed</span>
            </div>
          </div>
        </div>

        <div className="results-footer">
          <p className="results-footer-text">
            A copy of your assessment summary has been sent to {email}.
          </p>
          <button
            className="btn-assessment-tertiary"
            onClick={onReturnHome}
          >
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntakeResults;
